export interface UserLocation {
  lat: number;
  long: number;
  accuracy: number;
}

export interface GeolocationError {
  code: number;
  message: string;
}

/**
 * Request user's current location with fallback strategy
 * First tries with high accuracy, if that times out, tries with lower accuracy
 */
export const requestUserLocation = (): Promise<UserLocation> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject({
        code: 0,
        message: "Geolocation is not supported by your browser"
      });
      return;
    }

    // Try with lower accuracy settings for faster response
    // This works better on desktop browsers and when GPS is not available
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          long: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        reject({
          code: error.code,
          message: getGeolocationErrorMessage(error.code),
        });
      },
      {
        enableHighAccuracy: false, // Changed to false for faster response
        timeout: 15000, // Increased to 15 seconds
        maximumAge: 300000, // 5 minutes cache - will use cached location if available
      }
    );
  });
};

function getGeolocationErrorMessage(code: number): string {
  switch (code) {
    case 1:
      return "Location permission denied. Please enable location access in your browser settings.";
    case 2:
      return "Location unavailable. Please check your device settings.";
    case 3:
      return "Location request timed out. Please try again.";
    default:
      return "Unable to retrieve your location.";
  }
}

/**
 * Check if geolocation is available
 */
export const isGeolocationAvailable = (): boolean => {
  return 'geolocation' in navigator;
};

/**
 * Check if geolocation permission is granted (if Permissions API is supported)
 */
export const checkGeolocationPermission = async (): Promise<PermissionState | 'prompt'> => {
  if (!navigator.permissions) {
    return 'prompt';
  }
  
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
    return result.state;
  } catch {
    return 'prompt';
  }
};

